import React, { useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import Tooltip, { CustomWidthTooltip } from "components/Tooltip/Tooltip";
import styles from "./ViewErrorLogs.module.scss";
import Pagination from "@mui/material/Pagination";
import { DatePicker } from "@mui/x-date-pickers";
import { CopyAll } from "@mui/icons-material";

import { useDispatch, useSelector } from "react-redux";
import {
  fetchErrorLogs,
  setPageNo,
  setFromDate,
  setToDate
} from "../../store/slices/errorLogSlice";

const ViewErrorLogs = () => {
  const dispatch = useDispatch();

  const {
    errorLogs,
    loader,
    pageNo,
    total,
    fromDate,
    toDate
  } = useSelector((state) => state.errorLog);

  const pageSize = 25;
  const totalPages = Math.ceil(total / pageSize);

  useEffect(() => {
    dispatch(fetchErrorLogs());
  }, [pageNo, fromDate, toDate]);

  const errorLogColumns = [
    {
      field: "Date",
      headerName: "Date",
      minWidth: 100,
      sortable: true,
      disableColumnMenu: true,
      renderCell: (params) => {
        const date = params.row?.createdAt || "-";
        if (!date) return;
        const [datePart] = date.split("T");
        const [dd, mm, yyyy] = datePart.split("-");
        return `${dd}/${mm}/${yyyy}`;
      }
    },
    {
      field: "url",
      headerName: "URL",
      minWidth: 180,
      sortable: true,
      disableColumnMenu: true,
      renderCell: (params) =>
        params.row?.url?.length > 25 ? (
          <Tooltip
            text={params.row?.url.slice(0, 25) + "..."}
            tooltip={params.row?.url}
          />
        ) : (
          params.row?.url || "-"
        ),
    },
    {
      field: "method",
      headerName: "Method",
      minWidth: 50,
      sortable: true,
      disableColumnMenu: true,
      renderCell: (params) => params.row?.method || " -",
    },
    {
      field: "message",
      headerName: "Message",
      minWidth: 140,
      sortable: true,
      disableColumnMenu: true,
      renderCell: (params) =>
        params.row?.message?.length > 20 ? (
          <CustomWidthTooltip
            text={params.row?.message.slice(0, 20) + "..."}
            tooltip={params.row?.message}
          />
        ) : (
          params.row?.message || "-"
        ),
    },
    {
      field: "body",
      headerName: "Body",
      minWidth: 180,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const text = params.row?.body || "-";
        const shortText = text?.length > 25 ? text.slice(0, 25) + "..." : text;

        const handleCopy = (e) => {
          e.stopPropagation();
          navigator.clipboard.writeText(text);
        };

        return (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            {text.length > 40 ? (
              <Tooltip text={shortText} tooltip={text} />
            ) : (
              shortText
            )}

            {text !== "-" && (
              <CopyAll style={{ cursor: "pointer" }} onClick={handleCopy} />
            )}
          </div>
        );
      },
    },

    {
      field: "host",
      headerName: "Host",
      minWidth: 60,
      sortable: true,
      disableColumnMenu: true,
      renderCell: (params) =>
        params.row?.host?.length > 30 ? (
          <Tooltip
            text={params.row?.host.slice(0, 30) + "..."}
            tooltip={params.row?.host}
          />
        ) : (
          params.row?.host || "-"
        ),
    },
    {
      field: "errorStackTrace",
      headerName: "Error Stack Trace",
      minWidth: 200,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const text = params.row?.errorStackTrace || "-";
        const shortText = text?.length > 35 ? text.slice(0, 35) + "..." : text;

        const handleCopy = (e) => {
          e.stopPropagation();
          navigator.clipboard.writeText(text);
        };

        return (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            {text.length > 40 ? (
              <Tooltip text={shortText} tooltip={text} />
            ) : (
              shortText
            )}

            {text !== "-" && (
              <CopyAll style={{ cursor: "pointer" }} onClick={handleCopy} />
            )}
          </div>
        );
      },
    },

  ];

  return (
    <>
      {/* FILTERS */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "12px",
          marginBottom: "10px",
        }}
      >
        <DatePicker
          label="From Date"
          value={fromDate}
          onChange={(val) => dispatch(setFromDate(val))}
          slotProps={{
            textField: {
              sx: {
                width: 140,
                "& .MuiInputBase-root": {
                  height: 50,
                  fontSize: "13px",
                },
              },
            },
          }}
        />

        <DatePicker
          label="To Date"
          value={toDate}
          onChange={(val) => dispatch(setToDate(val))}
          slotProps={{
            textField: {
              sx: {
                width: 140,
                "& .MuiInputBase-root": {
                  height: 50,
                  fontSize: "13px",
                },
              },
            },
          }}
        />
      </div>

      {/* TABLE */}
      <div
        style={{
          overflow: "auto",
          height: "calc(100vh - 220px)",
          marginTop: "16px",
          scrollbarWidth: "none",
        }}
      >
        <DataGrid
          rows={errorLogs || []}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row"
          }
          autoHeight={false}
          columns={errorLogColumns}
          loading={loader}
          stickyHeader
          // disableColumnResize={true}
          sx={{
            borderRadius: "12px",
            border: "none",
            maxHeight: 500,
            overflow: "auto",
            fontSize: "12px",
            fontFamily: "'DM Sans', Arial",
            fontWeight: 500,
            "& .MuiDataGrid-scrollbar--vertical": {
              width: "0px !important",
              display: "none !important",
            },
            "& .MuiDataGrid-row--borderBottom": {
              color: "transparent",
            },
            "& .MuiDataGrid-scrollbarFiller": {
              background: "transparent !important",
              borderTop: "1px solid #F2F4F7 !important",
              borderBottom: "none !important",
            },
            "& .MuiDataGrid-scrollbarFiller--header": {
              background: "#f8f9fa !important",
            },
            "& .MuiDataGrid-filler": {
              background: "#f8f9fa !important",
            },
            "& .MuiDataGrid-columnSeparator": {
              display: "none !important",
            },
            "& .MuiDataGrid-root": {
              border: "none !important",
              borderWidth: "0px !important",
              display: "none !important",
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "14px",
              color: "#191722",
              scrollbarWidth: "none",
              overflow: "unset !important",
            },
            "& .MuiDataGrid-columnHeader": {
              position: "sticky !important",
              top: 0,
              backgroundColor: "#f8f9fa",
              zIndex: 1,
              color: "#444",
              fontWeight: 600,
              fontSize: "13.5px",
              borderBottom: "none !important",
              height: "56px !important",
              overflow: "unset",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
              color: "#686A83",
              whiteSpace: "nowrap",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "none !important",
              display: "flex",
              alignItems: "center",
              whiteSpace: "nowrap",
              overflow: "unset",
              textOverflow: "ellipsis",
              padding: "12px",
              fontWeight: "500",
              color: "#191722",
            },
            "& .MuiDataGrid-row": {
              backgroundColor: "#fff !important",
              minHeight: "52px !important",
              maxHeight: "52px !important",
              "--rowBorderColor": "#F2F4F7 !important",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#f6f7f8",
            },
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: "none",
            },
            "& .MuiDataGrid-footerContainer": {
              justifyContent: "center",
              padding: "16px 0",
              display: "none !important",
            },
            "& .MuiTablePagination-root": {
              display: "none",
            },
          }}
        />
      </div>

      {/* PAGINATION */}
      <div className={styles.paginationDiv}>
        {totalPages > 1 && (
          <Pagination
            count={totalPages}
            shape="rounded"
            variant="outlined"
            page={pageNo}
            onChange={(e, value) => dispatch(setPageNo(value))}
          />
        )}
      </div>
    </>
  );
};

export default ViewErrorLogs;
